// Settings — currently just the IP allowlist for /api/admin/*. More
// keys can land here later (cache TTLs, default rate-limit bursts).

import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { APIError, request } from '@/lib/api'
import type {
  AdminIPAllowlistResponse,
  AdminIPAllowlistUpdate,
  AdminMyIPResponse,
} from '@/types/api'

export function SettingsPage() {
  return (
    <div className="p-6">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-ink-400">Runtime knobs the admin layer reads on every request.</p>
      </header>

      <IPAllowlistCard />
    </div>
  )
}

function IPAllowlistCard() {
  const queryClient = useQueryClient()
  const cfg = useQuery({
    queryKey: ['admin', 'settings', 'ip-allowlist'],
    queryFn: () => request<AdminIPAllowlistResponse>('/admin/settings/ip-allowlist'),
  })
  const save = useMutation({
    mutationFn: (body: AdminIPAllowlistUpdate) =>
      request<AdminIPAllowlistResponse>('/admin/settings/ip-allowlist', { method: 'PUT', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'settings', 'ip-allowlist'] }),
  })
  const myIp = useQuery({
    queryKey: ['admin', 'settings', 'my-ip'],
    queryFn: () => request<AdminMyIPResponse>('/admin/settings/my-ip'),
  })

  const [enabled, setEnabled] = useState(false)
  const [ipsText, setIpsText] = useState('')

  useEffect(() => {
    if (cfg.data) {
      setEnabled(cfg.data.enabled)
      setIpsText(cfg.data.ips.join('\n'))
    }
  }, [cfg.data])

  const ips = ipsText
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean)

  const willLockoutSelf = enabled && ips.length === 0

  return (
    <div className="max-w-xl rounded-xl border border-ink-100 bg-white p-5 shadow-sm">
      <h2 className="text-base font-semibold tracking-tight">IP allowlist</h2>
      <p className="mt-1 text-xs text-ink-400">
        Gates every <code className="rounded bg-ink-50 px-1 font-mono">/api/admin/*</code> request.
        Trusts the nginx-set <code className="font-mono">X-Real-IP</code> only.
      </p>

      {save.error instanceof APIError && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {save.error.message}
        </div>
      )}

      <div className="mt-4 flex items-center gap-3">
        <label className="inline-flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="h-4 w-4 rounded border-ink-200 text-brand-500 focus:ring-brand-200"
          />
          <span className="font-medium text-ink-700">Enabled</span>
        </label>
        {willLockoutSelf && (
          <span className="rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
            Empty list + enabled = LOCKOUT
          </span>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
        <span className="text-ink-500">Your current IP:</span>
        <code className="rounded bg-ink-50 px-1.5 py-0.5 font-mono text-ink-700">
          {myIp.isLoading ? '…' : myIp.data?.ip ?? 'unknown'}
        </code>
        {myIp.data?.ip && !ips.includes(myIp.data.ip) && (
          <button
            type="button"
            onClick={() => {
              const ip = myIp.data?.ip
              if (!ip) return
              setIpsText((t) => (t.trim() ? `${t.trim()}\n${ip}` : ip))
            }}
            className="rounded border border-brand-200 px-2 py-0.5 text-xs font-medium text-brand-600 transition hover:bg-brand-50"
          >
            Add to allowlist
          </button>
        )}
        {myIp.data?.ip && ips.includes(myIp.data.ip) && (
          <span className="text-xs font-medium text-emerald-600">✓ in allowlist</span>
        )}
      </div>

      <label className="mt-4 block text-sm">
        <span className="mb-1 block text-xs font-medium text-ink-500">Allowed IPs (one per line)</span>
        <textarea
          rows={6}
          value={ipsText}
          onChange={(e) => setIpsText(e.target.value)}
          placeholder="1.2.3.4"
          className="w-full rounded-md border border-ink-200 px-3 py-2 font-mono text-xs focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
        />
      </label>

      <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
        Recovery if you lock yourself out: SSH to prod and run{' '}
        <code className="font-mono">python -m scripts.disable_ip_allowlist</code>.
      </div>

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          disabled={save.isPending}
          onClick={() => {
            if (willLockoutSelf && !confirm('Enabled + empty list locks EVERYONE out. Continue?')) return
            save.mutate({ enabled, ips })
          }}
          className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:opacity-60"
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  )
}
